<template>
  <div
    v-if="results.length > 0"
    class="rounded-xl shadow-card"
    :class="mode === 'dark' ? 'bg-dark-70' : 'bg-white-100'"
  >
    <div
      class="px-4 py-3.5 border-b"
      :class="mode === 'dark' ? 'border-dark-50' : ''"
    >
      <h3 class="font-semibold text-[17px] leading-none mb-2">Users</h3>
      <p
        class="text-[15px] leading-none"
        :class="mode === 'dark' ? 'text-dark-15' : 'text-gray-600'"
      >
        Total {{ results.length }}
        {{ results.length > 1 ? "results" : "result" }}
        found
      </p>
    </div>
    <div
      class="w-full overflow-x-auto without-scrollbar max-h-[500px] overflow-y-auto"
    >
      <table class="form-table w-full">
        <thead
          class="border-b-2"
          :class="mode === 'dark' ? 'border-dark-50' : ''"
        >
          <tr
            class="bg-opacity-20"
            :class="mode === 'dark' ? 'bg-dark-50' : 'bg-gray-20'"
          >
            <th @click="sortData('email')">
              <span class="flex items-center gap-1.5">
                <DTSort title="Email" :mode="props.mode"
              /></span>
            </th>
            <th @click="sortData('first_name')">
              <span class="flex items-center gap-1.5">
                <DTSort title="First name" :mode="props.mode"
              /></span>
            </th>
            <th @click="sortData('last_name')">
              <span class="flex items-center gap-1.5">
                <DTSort title="Last name" :mode="props.mode"
              /></span>
            </th>
            <th @click="sortData('date_joined')">
              <span class="flex items-center gap-1.5">
                <DTSort title="Join date" :mode="props.mode"
              /></span>
            </th>
            <th @click="sortData('tier')">
              <span class="flex items-center gap-1.5">
                <DTSort title="Tier" :mode="props.mode"
              /></span>
            </th>
          </tr>
        </thead>
        <tbody
          class="divide-y bg-opacity-40"
          :class="
            mode === 'dark'
              ? 'bg-dark-70 divide-dark-50'
              : 'bg-white-100 divide-gray-100'
          "
        >
          <tr v-for="(result, key) in results" :key="key">
            <td>{{ result.email }}</td>
            <td>{{ result.first_name }}</td>
            <td>{{ result.last_name }}</td>
            <td>{{ formatDate(result.date_joined) }}</td>
            <td class="capitalize">{{ result.tier }}</td>
          </tr>
        </tbody>
      </table>
    </div>
    <nav
      v-if="hasNextPage || currentPage > 1"
      class="border-t-4 rounded-bl-xl rounded-br-xl"
      :class="mode === 'dark' ? 'bg-dark-70 border-dark-50' : 'bg-white-100 '"
    >
      <ul class="flex items-center gap-2 px-4 py-3">
        <li>
          <button
            :disabled="currentPage === 1"
            class="button primary-button"
            @click="loadUsers(currentPage - 1)"
          >
            <ArrowLeft :size="20" :stroke-width="1.75" class="mt-[2px]" />
            Previous
          </button>
        </li>
        <li>
          <button
            :disabled="!hasNextPage"
            class="button primary-button"
            @click="loadUsers(currentPage + 1)"
          >
            Next
            <ArrowRight :size="20" :stroke-width="1.75" class="mt-[2px]" />
          </button>
        </li>
      </ul>
    </nav>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from "vue";
import type { Ref } from "vue";
import { useRouter } from "vue-router";
import ApiRequest from "../libs/ApiRequest";
import helper from "../libs/helper";
import { useUserStore } from "../stores/user";
import { User, UserReport } from "../libs/types";
import { customOrder, formatDate } from "../libs";
import DTSort from "./DataTableSortComponent.vue";
import { ArrowLeft, ArrowRight } from "lucide-vue-next";
interface Props {
  mode: string;
}
const userStore = useUserStore();
const props = defineProps<Props>();
const results: Ref<User[]> = ref([]);
const currentPage = ref(1);
const hasNextPage = ref(false);
const router = useRouter();
onMounted(() => {
  loadUsers(1);
});
const loadUsers = async (page: number) => {
  try {
    page = page < 1 ? 1 : page;
    const URL = `/api/auth/userlist?page=${page}`;
    const { data, status } = await ApiRequest().get<UserReport>(URL);
    if (status !== 200) {
      throw new Error("Something went wrong");
    }
    results.value = data.results;
    hasNextPage.value = Boolean(data.next);
    currentPage.value = page;
  } catch (error) {
    const extractedError = helper.extractAlert(error);
    if (extractedError.redirect) {
      userStore.signOut();
      router.push({ name: extractedError.redirect });
    }
  }
};
const sortData = (by: string) => {
  results.value = customOrder(results.value, by);
};
</script>
